import React, { useState, useEffect } from "react";
import { withAuth } from "../api/auth.ts";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Stack,
  Divider,
  Box,
  alpha,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  DriveFileMove as MoveIcon,
  FolderShared as SharedFolderIcon,
} from "@mui/icons-material";
import { PopButton } from "./PopButton.tsx";
import { FileItemDto } from "./FileManager.tsx";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import { formatFileSize, formatDate } from "../utils/formatUtils.ts"; // フォーマット関数があれば使用

interface DetailPanelProps {
  file: FileItemDto;
  palette: PaletteColor; // 色情報を受け取るように追加
  honbuId: number;
  roomId: number;
  onClose: () => void;
  onDelete: (id: string, type: "file") => void;
  onRename: (file: FileItemDto) => void;
  onCopy: (file: FileItemDto) => void;
  onToggleBookmark: (file: FileItemDto) => void;
  onMoveToFolder?: (file: FileItemDto) => void;
  onCopyToFolder?: (file: FileItemDto) => void;
}

export const DetailPanel: React.FC<DetailPanelProps> = ({
  file,
  palette,
  honbuId,
  roomId,
  onClose,
  onDelete,
  onRename,
  onCopy,
  onToggleBookmark,
  onMoveToFolder,
  onCopyToFolder,
}) => {
  const [optimisticBookmarked, setOptimisticBookmarked] = useState(
    file.isBookmarked,
  );

  useEffect(() => {
    setOptimisticBookmarked(file.isBookmarked);
  }, [file.isBookmarked]);

  const downloadUrl = withAuth(`/api/files/download/${file.fileId}?honbuId=${honbuId}&roomId=${roomId}`);

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="xs" // 幅を狭くしてスマホのメニューっぽく
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          p: 0,
        },
      }}
    >
      {/* グラデーションヘッダー */}
      <Box
        sx={{
          background: `linear-gradient(to right, ${PaletteColorHex[palette]}, ${alpha(PaletteColorHex[palette], 0.7)})`,
          color: "#fff",
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            mr: 1,
          }}
        >
          {file.fileName}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff", flexShrink: 0 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ height: 3, bgcolor: PaletteColorHex[palette] }} />

      <DialogContent>
        {/* ボタン群 (縦1列) */}
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* 1. ダウンロード (Primary色) */}
          <PopButton
            palette={palette}
            popVariant="primary"
            href={downloadUrl} // リンクとして機能させる
            startIcon={<DownloadIcon />}
            fullWidth
          >
            ダウンロード
          </PopButton>

          {/* 2. コピーを作成 (White) */}
          <PopButton
            palette={palette}
            popVariant="white"
            onClick={() => onCopy(file)}
            startIcon={<CopyIcon />}
            fullWidth
          >
            コピーを作成
          </PopButton>

          {/* 別のフォルダに移動/コピー */}
          {onMoveToFolder && (
            <PopButton
              palette={palette}
              popVariant="white"
              onClick={() => onMoveToFolder(file)}
              startIcon={<MoveIcon />}
              fullWidth
            >
              別のフォルダに移動...
            </PopButton>
          )}
          {onCopyToFolder && (
            <PopButton
              palette={palette}
              popVariant="white"
              onClick={() => onCopyToFolder(file)}
              startIcon={<SharedFolderIcon />}
              fullWidth
            >
              別のフォルダにコピー...
            </PopButton>
          )}

          {/* 3. 名前を変更 (White) */}
          <PopButton
            palette={palette}
            popVariant="white"
            onClick={() => onRename(file)}
            startIcon={<EditIcon />}
            fullWidth
          >
            名前を変更
          </PopButton>

          {/* 4. ブックマークを付ける/外す (White) */}
          <PopButton
            palette={palette}
            popVariant="white"
            onClick={() => {
              setOptimisticBookmarked((prev: boolean) => !prev);
              onToggleBookmark(file);
            }}
            startIcon={
              optimisticBookmarked ? (
                <BookmarkIcon sx={{ color: "#FFB020" }} />
              ) : (
                <BookmarkBorderIcon />
              )
            }
            fullWidth
          >
            {optimisticBookmarked
              ? "ブックマークを外す"
              : "ブックマークを付ける"}
          </PopButton>

          {/* 5. 削除 (White - 赤文字強調) */}
          <PopButton
            palette={palette}
            popVariant="white"
            onClick={() => {
              onDelete(file.fileId, "file");
              onClose(); // 削除したら閉じる
            }}
            startIcon={<DeleteIcon color="error" />}
            fullWidth
            sx={{
              color: "error.main",
              borderColor: "error.main",
              "&:hover": {
                borderColor: "error.dark",
                bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
              },
            }}
          >
            削除
          </PopButton>
        </Stack>

        {/* 区切り線 */}
        <Divider sx={{ my: 3 }} />

        {/* 詳細情報エリア */}
        <Box sx={{ px: 1 }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  アップロード日
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {formatDate
                    ? formatDate(file.updatedAt)
                    : new Date(file.updatedAt).toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  サイズ
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {formatFileSize
                    ? formatFileSize(file.fileSize)
                    : `${(file.fileSize / 1024).toFixed(1)} KB`}
                </Typography>
              </Box>
            </Stack>
            {file.uploaderName && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  アップロード者
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {file.uploaderName}
                  {file.uploaderDept && (
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      ({file.uploaderDept})
                    </Typography>
                  )}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
